import api from './api';
import { BusCheckoutData, BusCheckoutListResponse } from '../interfaces/BusCheckout';
import { CheckoutHistoryItem } from '../interfaces/Checkout';

export interface CreateManualBusCheckoutDto {
  email: string;
  fone_payment: string;
  name: string;
  total_value: number;
  trip_ida_data: {
    id: string;
    max_split_value_markup: number;
    cidade_origem_slug: string;
    cidade_destino_slug: string;
    origem_slug: string;
    destino_slug: string;
    datetime_ida: string;
    chegada_ida: string;
    max_split_value: number;
    company_name: string;
    tipo_assento: string;
    tipo_veiculo: string;
    vehicle_plate?: string;
    is_conexao?: boolean;
    conexoes?: any[];
  };
  trip_volta_data?: {
    id: string;
    max_split_value_markup: number;
    cidade_origem_slug: string;
    cidade_destino_slug: string;
    origem_slug: string;
    destino_slug: string;
    datetime_ida: string;
    chegada_ida: string;
    max_split_value: number;
    company_name: string;
    tipo_assento: string;
    tipo_veiculo: string;
    vehicle_plate?: string;
    is_conexao?: boolean;
    conexoes?: any[];
  };
  passengers: {
    name: string;
    cpf: string;
    birthDate: string;
    nationality?: string;
    rg?: string;
    phone_number?: string;
    seat_number?: string;
  }[];
  reserved_code_company?: string;
  notes?: string;
}

export interface UpdateCheckoutBusFieldsDto {
  updatedBy: string;
  reason?: string;
  datetime_ida?: string;
  chegada_ida?: string;
  volta_datetime_ida?: string;
  volta_chegada_ida?: string;
  baseprice?: number;
  baseprice_markup?: number;
  vehicle_plate?: string;
  reserved_code_company?: string;
  emission_partner?: string;
  volta_vehicle_plate?: string;
  volta_reserved_code_company?: string;
  volta_emission_partner?: string;
}

export interface UpdateBusPassengerFieldsDto {
  updatedBy: string;
  reason: string;
  name?: string;
  birth_date?: string;
  rg?: string;
  cpf?: string;
  phone_number?: string;
  seat_number?: string;
  volta_seat_number?: string;
}

export interface ResendBusNotificationDto {
  channels: ('email' | 'sms' | 'whatsapp')[];
  template?: string;
  passengerId?: number;
  updatedBy: string;
  reason?: string;
}

export const BUS_EMISSION_PARTNERS = [
  { value: 'aguia_branca',  label: 'Águia Branca' },
  { value: 'catedral',      label: 'Catedral' },
  { value: 'queropassagem', label: 'QueroPassagem' },
  { value: 'clickbus',      label: 'ClickBus' },
  { value: 'buser',         label: 'Buser' },
] as const;

export type BusEmissionPartner = typeof BUS_EMISSION_PARTNERS[number]['value'];

export const busCheckoutService = {
  async getBusCheckouts(
    page: number = 1,
    limit: number = 10,
    filters?: {
      status?: string;
      paymentStatus?: string;
      pedidoId?: string;
      passengerName?: string;
      routeOutbound?: string;
      routeReturn?: string;
      minValue?: string;
      maxValue?: string;
      dateFrom?: string;
      dateTo?: string;
    },
  ): Promise<BusCheckoutListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.status)        params.append('status', filters.status);
    if (filters?.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
    if (filters?.pedidoId)      params.append('pedidoId', filters.pedidoId);
    if (filters?.passengerName) params.append('passengerName', filters.passengerName);
    if (filters?.routeOutbound) params.append('routeOutbound', filters.routeOutbound);
    if (filters?.routeReturn)   params.append('routeReturn', filters.routeReturn);
    if (filters?.minValue)      params.append('minValue', filters.minValue);
    if (filters?.maxValue)      params.append('maxValue', filters.maxValue);
    if (filters?.dateFrom)      params.append('dateFrom', new Date(filters.dateFrom).toISOString());
    if (filters?.dateTo)        params.append('dateTo', new Date(filters.dateTo + 'T23:59:59').toISOString());

    const response = await api.get(`/bus/checkout?${params}`);
    return response.data;
  },

  async getBusCheckoutById(id: string): Promise<BusCheckoutData> {
    const response = await api.get(`/bus/checkout/${id}`);
    return response.data.data || response.data;
  },

  async updateCheckoutStatus(
    id: string, 
    status: string, 
    updatedBy: string,
    additionalData?: {
      vehicle_plate?: string;
      reserved_code_company?: string;
      emission_partner?: BusEmissionPartner;
      emitted_total_price?: number;
      seat_numbers?: string[];
      volta_vehicle_plate?: string;
      volta_reserved_code_company?: string;
      volta_emission_partner?: BusEmissionPartner;
      volta_seat_numbers?: string[];
    }
  ): Promise<BusCheckoutData> {
    const response = await api.patch(`/bus/checkout/${id}/status`, {
      status,
      updatedBy,
      ...additionalData
    });
    return response.data;
  },

  async updatePaymentStatus(id: string, paymentStatus: 'processando' | 'aprovado' | 'reprovado' | 'reembolsado', updatedBy: string): Promise<BusCheckoutData> {
    const response = await api.patch(`/bus/checkout/${id}/payment-status`, {
      paymentStatus,
      updatedBy
    });
    return response.data;
  },

  async updateCheckoutFields(id: string, dto: UpdateCheckoutBusFieldsDto): Promise<any> {
    const response = await api.patch(`/bus/checkout/${id}/fields`, dto);
    return response.data;
  },

  async updatePassengerFields(checkoutId: string, passengerId: number, dto: UpdateBusPassengerFieldsDto): Promise<any> {
    const response = await api.patch(`/bus/checkout/${checkoutId}/passenger/${passengerId}/fields`, dto);
    return response.data;
  },

  async resendNotification(id: string, dto: ResendBusNotificationDto): Promise<any> {
    const response = await api.post(`/bus/checkout/${id}/notify`, dto);
    return response.data;
  },

  async getCheckoutHistory(id: string): Promise<CheckoutHistoryItem[]> {
    const response = await api.get(`/bus/checkout/${id}/history`);
    return response.data;
  },

  async getWhatsappEvents(id: string): Promise<any> {
    const response = await api.get(`/bus/checkout/${id}/whatsapp-events`);
    return response.data;
  },

  async createManualCheckout(dto: CreateManualBusCheckoutDto): Promise<any> {
    const response = await api.post('/bus/checkout/manual', dto);
    return response.data;
  },

  async getBoardingPasses(checkoutBusId: string): Promise<any[]> {
    const response = await api.get(`/bus/checkout/${checkoutBusId}/boarding-pass`);
    return response.data;
  },

  async uploadBoardingPass(checkoutBusId: string, formData: FormData): Promise<any> {
    const response = await api.post(`/bus/checkout/${checkoutBusId}/boarding-pass`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async sendBoardingPass(checkoutBusId: string, boardingPassId: string): Promise<any> {
    const response = await api.post(`/bus/checkout/${checkoutBusId}/boarding-pass/send`, { boardingPassId });
    return response.data;
  },

  async deleteBoardingPass(checkoutBusId: string, boardingPassId: string): Promise<void> {
    await api.delete(`/bus/checkout/${checkoutBusId}/boarding-pass/${boardingPassId}`);
  },
};
